import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Snackbar,
  Alert,
} from '@mui/material';
import { RocketLaunch } from '@mui/icons-material';
import { getServices, getConnections as getDevopsConnections, deployService, getJobOutput } from '../services/devopsAPIService';

// Logs polling now uses real job-output endpoint

const ManageService = () => {
  const [connections, setConnections] = useState([]);
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [logsHtml, setLogsHtml] = useState('');
  const [form, setForm] = useState({ service: '', server: '' });

  const logRef = useRef(null);
  const pollRef = useRef(null);

  const defaultLogsText = 'Fetching Logs ...';

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [c, s] = await Promise.all([
          getDevopsConnections(),
          getServices(),
        ]);
        if (!mounted) return;
        setConnections(Array.isArray(c) ? c : []);
        setServices(Array.isArray(s) ? s : []);
      } catch (e) {
        if (!mounted) return;
        setConnections([]);
        setServices([]);
        const msg = e?.message || 'Failed to load data';
        setSnackbar({ open: true, message: msg, severity: 'error' });
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    // auto-scroll logs
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [logsHtml]);

  const canDeploy = useMemo(() => !!form.service && !!form.server, [form]);

  const startPollingLogs = (jobId) => {
    setLogsHtml(defaultLogsText);
    const intervalMs = 3000;
    const run = async () => {
      try {
        const out = await getJobOutput(jobId, 'json');
        const job = out?.job || {};
        const log = out?.log || {};
        const content = (typeof log?.content === 'string') ? log.content : '';
        const statusLine = `Job ${jobId}: ${job.status || 'pending'}${job.elapsedTime != null ? ` | t+${Math.floor(job.elapsedTime)}s` : ''}`;
        setLogsHtml((_) => `${statusLine}${content ? `<br/>${content.replace(/\n/g, '<br/>')}` : ''}`);
        const finished = (job.status || '').toLowerCase() === 'successful' || (job.status || '').toLowerCase() === 'failed' || (job.status || '').toLowerCase() === 'error' || (job.finishedAt != null);
        if (finished) {
          setIsLoading(false);
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (e) {
        setIsLoading(false);
        clearInterval(pollRef.current);
        pollRef.current = null;
        const apiMsg = e?.message || 'Failed to fetch logs';
        setSnackbar({ open: true, message: apiMsg, severity: 'error' });
      }
    };
    run();
    pollRef.current = setInterval(run, intervalMs);
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const handleDeploy = async () => {
    if (!canDeploy) return;
    setIsLoading(true);
    setSnackbar({ open: false, message: '', severity: 'success' });
    try {
      const requestBody = {
        service: form?.service?.service,
        serviceName: form?.service?.name,
        projectId: form?.service?.projectId,
        connectionId: form?.server,
      };
      const res = await deployService(requestBody);
      setSnackbar({ open: true, message: (res && (res.message || res.msg)) || 'Deployment started. Please keep this page open to view logs.', severity: 'success' });
      const jobId = res?.jobId || res?.data?.jobId;
      if (jobId) {
        startPollingLogs(jobId);
      } else {
        setIsLoading(false);
      }
    } catch (e) {
      setIsLoading(false);
      const apiMsg = e?.message || 'Failed to initiate deployment. Try again.';
      setSnackbar({ open: true, message: apiMsg, severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Manage Service</Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
        <FormControl fullWidth size="small" required>
          <InputLabel>Service</InputLabel>
          <Select
            label="Service"
            value={form.service}
            onChange={(e) => setForm((p) => ({ ...p, service: e.target.value }))}
          >
            {services.map((s, i) => (
              <MenuItem key={i} value={s}>{s.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small" required>
          <InputLabel>Server</InputLabel>
          <Select
            label="Server"
            value={form.server}
            onChange={(e) => setForm((p) => ({ ...p, server: e.target.value }))}
          >
            {connections.map((c) => (
              <MenuItem key={c.connectionId} value={c.connectionId}>
                {c.connectionName} ({c.hostName})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          onClick={handleDeploy}
          disabled={!canDeploy || isLoading}
          startIcon={<RocketLaunch />}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            width: { xs: '100%', md: 'auto' },
            alignSelf: { xs: 'stretch', md: 'auto' },
            px: 2.5,
            minWidth: { md: 160 },
          }}
        >
          {isLoading ? 'Deploying...' : 'Deploy'}
        </Button>
      </Stack>

      <Paper sx={{ mt: 3, p: 2, borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Logs</Typography>
        <Paper
          variant="outlined"
          sx={{ p: 2, height: 320, overflowY: 'auto', borderRadius: 2, bgcolor: '#0b1220', color: '#e5e7eb' }}
          ref={logRef}
        >
          <pre style={{ margin: 0 }} dangerouslySetInnerHTML={{ __html: logsHtml }} />
        </Paper>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageService;
